import React from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/form/Button/Button";
import { TextField } from "@/components/form/text-field/TextField";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { TooltipOnFocus } from "@/components/ui/tooltip/Tooltip2";
import { ConditionalRender } from "@/lib/conditional-render/ConditionalRender";
import { projectIs } from "@/lib/feature-flags/featureFlagsDeside.helpers";
import { SignUpLoginRedirectResDto } from "@/models/infoModalRes.dto";
import { FacebookRegistrationButton } from "@/shared/facebook-registration-button/FacebookRegistrationButton";
import { GoogleRegistrationButton } from "@/shared/google-registration-button/GoogleRegistrationButton";
import { pickFirstTrueMatchingValue } from "@/utils/pickFirstTrueMatchingValue.utils";
import { clsxm } from "@/utils/tailwindMerge";

import { useSendSignInForm } from "../forms/sign-in-form/hooks";
import { isErrorSignUpResDto, useSendSignUpForm } from "./hooks";
import { SignUpFormSchema } from "./models";

type PropTypes = {
  skipHeader?: boolean;
  skipFirstParagraph?: boolean;
  header?: React.ReactNode;
  textCentered?: boolean;
  ladyIdRedirectParam?: string;
};

export const SignUpForm: React.FC<PropTypes> = ({
  skipHeader,
  skipFirstParagraph,
  header,
  textCentered,
  ladyIdRedirectParam,
}) => {
  const { mutateAsync: signUpSend } = useSendSignUpForm();
  const { mutate: firstTimeSignInSend } = useSendSignInForm({
    isLoginFromRegistration: true,
    ladyIdRedirectParam,
  });
  const { mutate: alreadyRegisteredSignInSend } = useSendSignInForm({
    isLoginFromRegistration: false,
    ladyIdRedirectParam,
  });

  const { register, handleSubmit } = useForm<SignUpFormSchema>({
    resolver: zodResolver(SignUpFormSchema),
  });

  const onSubmitHandler = handleSubmit(async (data) => {
    const { username, userpass, firstName } = data;

    const signUpRes = await signUpSend({ username, userpass, firstName });

    const isAlreadyRegistered =
      SignUpLoginRedirectResDto.safeParse(signUpRes).success;

    if (isAlreadyRegistered) {
      alreadyRegisteredSignInSend({ username, userpass });
      return;
    }

    if (isErrorSignUpResDto(signUpRes)) {
      return;
    }

    firstTimeSignInSend({ username, userpass });
  });

  const { removeAll } = useOpenModal();

  return (
    <form
      className={clsxm("flex flex-col gap-3", textCentered && "text-center")}
      onSubmit={onSubmitHandler}
    >
      <ConditionalRender on={!skipHeader}>
        <span className="text-2xl text-pink font-bold">
          {textContent.header}
        </span>
      </ConditionalRender>

      {header}

      <ConditionalRender on={!skipFirstParagraph}>
        <p className="text-[13px] lg:text-sm">
          {textContent.completeTheRegistration}
        </p>
      </ConditionalRender>
      <TooltipOnFocus
        placement="top"
        title="Enter your first name here: it must contain only letters. From 2 to 50 characters."
      >
        <TextField
          type="text"
          label="Name"
          inputProps={{ pattern: "[a-zA-Z]{2,50}" }}
          {...register("firstName")}
        />
      </TooltipOnFocus>
      <TooltipOnFocus
        placement="top"
        title="It can't be empty or more than 100 symbols!"
      >
        <TextField
          type="email"
          label="Email"
          inputProps={{ pattern: "[\\s\\S]{1,100}" }}
          {...register("username")}
        />
      </TooltipOnFocus>
      <TooltipOnFocus
        placement="top"
        title="It can't be empty or contain spaces and should be between 6 and 25 symbols"
      >
        <TextField
          type="password"
          label="Password"
          inputProps={{
            pattern:
              "[0-9a-zA-Z!@#$%^&*~\\/\\|_+=\\{\\}:;',.<>?\\[\\]\\-\"\\\\\\(\\)]{6,25}",
          }}
          {...register("userpass")}
        />
      </TooltipOnFocus>
      <div className="flex justify-between gap-2 items-end">
        <div className="flex gap-2">
          <GoogleRegistrationButton />
          <FacebookRegistrationButton />
        </div>
        <Button
          variant="primary"
          className="text-[15px] w-full h-9 rounded-full"
          type="submit"
          onHoverVariant="color-reverse"
        >
          {textContent.submitButtonText}
        </Button>
      </div>
      {projectIs(["GM"]) ? (
        <FormBottomContentGM removeAll={removeAll} />
      ) : (
        <FormBottomContent removeAll={removeAll} />
      )}
    </form>
  );
};

const textContent = pickFirstTrueMatchingValue([
  [
    projectIs(["GM"]),
    {
      header: <>REGISTER ON GOLDEN&nbsp;MEETS</>,
      completeTheRegistration:
        "Complete the registration form and receive FREE membership and your personal ID to Golden Meets",
      submitButtonText: "MAKE NEW CONNECTIONS",
    },
  ],
  [
    true,
    {
      header: <>REGISTER ON GOLDEN&nbsp;BRIDE</>,
      completeTheRegistration:
        "Complete the registration form and get the FREE membership and your personal ID to Golden Bride!",
      submitButtonText: "Find Your Matches",
    },
  ],
]);

type FormBottomContentPropTypes = {
  removeAll: () => void;
};

const FormBottomContent: React.FC<FormBottomContentPropTypes> = ({
  removeAll,
}) => {
  return (
    <p className="text-[13px] lg:text-sm">
      By clicking “Find Your Matches” you confirm you are 18+ and agree to the{" "}
      {/* // TODO: WEB-1726 change to Link */}
      <a
        // href={"/page/TERMS"}
        href={"/page?TERMS"}
        className="text-pink underline"
        onClick={removeAll}
      >
        terms and conditions.
      </a>{" "}
      If you have difficulties with registration, please{" "}
      {/* // TODO: WEB-1726 change to Link */}
      <a
        // href={"/page/CONTACT"}
        href={"/page?CONTACT"}
        className="text-pink underline"
        onClick={removeAll}
      >
        contact us.
      </a>
    </p>
  );
};

const FormBottomContentGM: React.FC<FormBottomContentPropTypes> = ({
  removeAll,
}) => {
  return (
    <p className="text-[13px] lg:text-sm">
      By clicking “MAKE NEW CONNECTIONS” you confirm you are 18+ and agree to
      the {/* // TODO: WEB-1726 change to Link */}
      <a
        // href={"/page/TERMS"}
        href={"/t_terms.html"}
        className="text-pink underline"
        onClick={removeAll}
      >
        terms and conditions.
      </a>{" "}
      If you have difficulties with registration, please{" "}
      {/* // TODO: WEB-1726 change to Link */}
      <a
        // href={"/page/CONTACT"}
        href={"/t_contact.html"}
        className="text-pink underline"
        onClick={removeAll}
      >
        contact us.
      </a>
    </p>
  );
};
